import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateEU, formatDateTimeEU } from "@/lib/date";
import { Plus, Pencil, Trash2, LogOut, Mail, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [contacts, setContacts] = useState([]);

  const loadAll = async () => {
    try {
      const [p, d, c] = await Promise.all([
        api.get("/blog/posts", { params: { published_only: false } }),
        api.get("/demo-requests"),
        api.get("/contact-requests"),
      ]);
      setPosts(p.data || []);
      setDemos(d.data || []);
      setContacts(c.data || []);
    } catch (_e) {}
  };

  useEffect(() => { loadAll(); }, []);

  const del = async (id) => {
    if (!window.confirm("Fshini këtë postim?")) return;
    try {
      await api.delete(`/blog/posts/${id}`);
      setPosts((ps) => ps.filter((x) => x.id !== id));
      toast.success("Postimi u fshi");
    } catch (e) {
      toast.error("Fshirja dështoi");
    }
  };

  return (
    <>
      <SEO title="Admin · Akomoda" noindex />
      <section className="border-b border-line bg-white">
        <div className="container-akm flex flex-wrap items-center justify-between gap-3 py-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate2">Admin</div>
            <h1 className="font-display text-2xl font-extrabold text-ink">Mirë se erdhe, {user?.name || "Admin"}</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/new" className="btn-primary" data-testid="admin-new-post"><Plus size={16} /> Postim i ri</Link>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={16} /> Dil</button>
          </div>
        </div>
      </section>

      <section className="bg-bg">
        <div className="container-akm py-8">
          <div className="mb-6 inline-flex rounded-md border border-line bg-white p-1">
            {[
              ["posts", `Postimet (${posts.length})`],
              ["demos", `Demo (${demos.length})`],
              ["contacts", `Kontakte (${contacts.length})`],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`rounded px-3 py-1.5 text-sm font-medium ${tab === k ? "bg-[#FF7A00] text-white" : "text-ink hover:bg-bg"}`}
                data-testid={`admin-tab-${k}`}
              >
                {l}
              </button>
            ))}
          </div>

          {tab === "posts" && (
            <div className="overflow-hidden rounded-xl border border-line bg-white">
              {posts.length === 0 && <div className="p-6 text-sm text-slate2">Asnjë postim ende. Krijoni të parin!</div>}
              {posts.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-4 last:border-b-0" data-testid={`admin-post-${p.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${p.published ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                        {p.published ? "I publikuar" : "Draft"}
                      </span>
                      <div className="font-display text-base font-bold text-ink truncate">{p.title}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate2">/blog/{p.slug} · {formatDateEU(p.created_at)}</div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/blog/${p.slug}`} className="btn-ghost py-1.5 text-xs">Shiko</Link>
                    <Link to={`/admin/edit/${p.id}`} className="btn-ghost py-1.5 text-xs"><Pencil size={14} /> Edito</Link>
                    <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 rounded-md border border-line bg-white px-3 py-1.5 text-xs font-semibold text-[#991B1B] hover:border-[#991B1B]">
                      <Trash2 size={14} /> Fshi
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "demos" && (
            <RequestList items={demos} emptyText="Asnjë kërkesë demo ende." icon={Calendar} />
          )}
          {tab === "contacts" && (
            <RequestList items={contacts} emptyText="Asnjë mesazh kontakti ende." icon={Mail} />
          )}
        </div>
      </section>
    </>
  );
}

function RequestList({ items, emptyText, icon: Icon }) {
  if (items.length === 0) {
    return <div className="card-akm text-sm text-slate2">{emptyText}</div>;
  }
  return (
    <div className="space-y-3">
      {items.map((r) => (
        <div key={r.id} className="rounded-xl border border-line bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon size={16} className="text-[#FF7A00]" />
              <span className="font-display text-base font-bold text-ink">{r.name}</span>
              <span className="text-sm text-slate2">· {r.email}</span>
            </div>
            <div className="text-xs text-slate2">{formatDateTimeEU(r.created_at)}</div>
          </div>
          <div className="mt-2 grid gap-2 text-sm text-slate2 sm:grid-cols-3">
            {r.phone && <div><span className="text-ink">Telefon:</span> {r.phone}</div>}
            {r.company && <div><span className="text-ink">Biznesi:</span> {r.company}</div>}
            {r.property_type && <div><span className="text-ink">Tipi:</span> {r.property_type}</div>}
            {r.rooms && <div><span className="text-ink">Dhoma:</span> {r.rooms}</div>}
            {r.subject && <div><span className="text-ink">Subjekti:</span> {r.subject}</div>}
          </div>
          {r.message && <div className="mt-2 rounded-md border border-line bg-bg p-3 text-sm text-slate2">{r.message}</div>}
        </div>
      ))}
    </div>
  );
}
